/**
 * `useGenerateCustomArt` — TH-55363.
 *
 * Wrapper TanStack Query do POST /custom-arts/generate. Cria uma
 * custom_art em status='draft'+generation_status='pending' e enfileira
 * task `generate_custom_art` no worker.
 *
 * Cliente faz polling em GET /custom-arts/:id até generation_status
 * virar 'ready' (preview disponível em art_url) ou 'failed'
 * (motivo em ai_error_message). Hook companheiro `useCustomArtStatus`
 * faz o polling com cadência de 2s e para quando termina.
 *
 * Backend (TH-55363) exige role tenant_admin ou super_admin.
 * 422 se circuit breaker de orçamento IA do tenant estiver aberto.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { CustomArtRead } from "@/hooks/useCreateCustomArt";

export type CustomArtCategory =
  | "hortifruti"
  | "acougue"
  | "padaria"
  | "mercearia"
  | "bebidas"
  | "promo"
  | "limpeza"
  | "sazonal";

/** Mapeamento label exibido na UI → slug enviado ao backend (TH-55172). */
export const CATEGORY_LABELS: Record<CustomArtCategory, string> = {
  hortifruti: "Hortifrúti",
  acougue: "Açougue",
  padaria: "Padaria",
  mercearia: "Mercearia",
  bebidas: "Bebidas",
  promo: "Promoção",
  limpeza: "Limpeza",
  sazonal: "Sazonal",
};

export interface GenerateCustomArtInput {
  name: string;
  category: CustomArtCategory;
  prompt?: string;
  product_id?: string;
  store_id?: string;
  area_id?: string;
}

export interface GenerateCustomArtResponse {
  art: CustomArtRead;
  /** Custo estimado (R$) — debitado de verdade só após a geração. */
  estimated_cost_brl: string;
}

async function postGenerateCustomArt(
  input: GenerateCustomArtInput,
): Promise<GenerateCustomArtResponse> {
  const res = await fetch("/api/custom-arts/generate", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }

  return (await res.json()) as GenerateCustomArtResponse;
}

export function useGenerateCustomArt(): UseMutationResult<
  GenerateCustomArtResponse,
  Error,
  GenerateCustomArtInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postGenerateCustomArt,
    onSuccess: () => {
      // A nova arte aparece em "Minhas" — invalida o cache de listagem.
      void queryClient.invalidateQueries({ queryKey: ["custom-arts"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Polling de status — para enquanto generation_status virar 'ready'|'failed'.
// ---------------------------------------------------------------------------

async function fetchCustomArt(id: string): Promise<CustomArtRead> {
  const res = await fetch(`/api/custom-arts/${id}`, {
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as CustomArtRead;
}

/**
 * Polling de status de geração — cadência 2s enquanto pending/generating.
 * Para automaticamente quando vira ready ou failed. Reaproveita o GET
 * /custom-arts/:id existente — sem endpoint novo.
 *
 * Passar `null` desabilita o polling (útil pra usar condicional dentro
 * de componentes).
 */
export function useCustomArtStatus(
  id: string | null,
): UseQueryResult<CustomArtRead, Error> {
  return useQuery({
    queryKey: ["custom-art", id],
    queryFn: () => fetchCustomArt(id as string),
    enabled: id !== null,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2000;
      const s = data.generation_status;
      if (s === "ready" || s === "failed") return false;
      return 2000;
    },
    // Manter o cache curto pra refletir status novos rapidamente.
    staleTime: 0,
  });
}
